import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query/react'; //
import {
  useGetTenantsQuery,
  useGetOrganizationsQuery,
  useGetWorkspaceQuery,
  useGetAvailableWorkspacesQuery,
  useUpdateWorkspaceMutation
} from '../../services/portal';

const EditWorkspace = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm();

  // Fetch tenants and organizations as usual
  const { data: tenantsData } = useGetTenantsQuery();
  const { data: organizationsData } = useGetOrganizationsQuery();
  const [availableWorkspaceCodes, setAvailableWorkspaceCodes] = useState([]);
  // Get workspace details by workspaceId
  const { data: workspaceData, isSuccess: workspaceLoaded } = useGetWorkspaceQuery(workspaceId);

  // Watch tenant id (we'll fix this to first tenant anyway)
  const tenantId = watch('tenant');

  // Get workspace codes filtered by tenantId (like in CreateWorkspace)
  const { data: workspaceCodesData } = useGetAvailableWorkspacesQuery(
    tenantId ? { tenantId } : skipToken
  );

  // Mutation for updating
  const [updateWorkspace, { isLoading: isUpdating }] = useUpdateWorkspaceMutation();

  // Fix tenant to first tenant from tenantsData, set tenant form value accordingly
  useEffect(() => {
    if (tenantsData?.tenants?.length > 0) {
      const firstTenant = tenantsData.tenants[0];
      setValue('tenant', firstTenant.tenant_id);
    }
  }, [tenantsData, setValue]);

  // When workspace loads, populate form values (except tenant, keep fixed)
  useEffect(() => {
    if (workspaceLoaded && workspaceData) {
      setValue('organization', workspaceData.organization?.organization_id || '');
      setValue('name', workspaceData.workspace_name || '');
      setValue('code', workspaceData.workspace_code || '');
      setValue('description', workspaceData.workspace_description || '');
    }
  }, [workspaceLoaded, workspaceData, setValue]);

  useEffect(() => {
    if (
      workspaceCodesData?.reportCodes?.length &&
      workspaceCodesData?.reportNames?.length &&
      workspaceCodesData.reportCodes.length === workspaceCodesData.reportNames.length
    ) {
      const codes = workspaceCodesData.reportCodes.map((code, index) => ({
        code,
        name: workspaceCodesData.reportNames[index],
      }));
      setAvailableWorkspaceCodes(codes);
    } else {
      setAvailableWorkspaceCodes([]);
    }
  }, [workspaceCodesData]);


  // Cancel button handler
  const handleCancel = () => navigate('/workspaces');

  // Form submit handler
  const onSubmit = async (formData) => {
    try {
      await updateWorkspace({
        workspace_id: workspaceId,
        workspace_name: formData.name,
        workspace_shortname: 'XX',
        workspace_code: formData.code,
        workspace_description: formData.description,
        organization: {
          organization_id: formData.organization
        },
        tenant: {
          tenant_id: formData.tenant
        }
      }).unwrap();

      navigate('/workspaces');
    } catch (err) {
      console.error('Update workspace error:', err);
      // optionally show error feedback here
    }
  };

  // Extract first tenant object for display
  const firstTenant = tenantsData?.tenants?.[0] || null;

  // Workspace codes filtered for tenant (default to empty array)
  const workspaceCodes = workspaceCodesData?.codes || [];

  return (
    <div className="page">
      <div className="page-inner">
        <header className="page-title-bar">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item active">
                <a href="/workspaces">
                  <i className="breadcrumb-icon fa fa-angle-left mr-2" />
                  Workspaces
                </a>
              </li>
            </ol>
          </nav>
          <div className="d-flex justify-content-between">
            <h1 className="page-title">Edit Workspace</h1>
          </div>
        </header>

        <div className="page-section">
          <div id="labels" className="card">
            <div className="card-body">
              <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <h6>Workspace</h6>

                {/* Tenant - fixed, read-only */}
                <div className="form-group col-md-6 mb-3">
                  <label>Tenant</label>
                  <input
                    type="text"
                    className="form-control"
                    value={
                      firstTenant
                        ? `${firstTenant.tenant_name} (${firstTenant.tenant_capacity_id})`
                        : ''
                    }
                    readOnly
                    disabled
                  />
                  <input
                    type="hidden"
                    {...register('tenant', { required: true })}
                    value={firstTenant?.tenant_id || ''}
                  />
                </div>

                {/* Organization - editable */}
                <div className="form-group col-md-6 mb-3">
                  <label htmlFor="organization">Organization <span className="text-danger">*</span></label>
                  <select
                    id="organization"
                    className="custom-select"
                    {...register('organization', { required: true })}
                  >
                    {organizationsData?.organizations?.map((org) => (
                      <option key={org.organization_id} value={org.organization_id}>
                        {org.organization_name}
                      </option>
                    ))}
                  </select>
                  {errors.organization && (
                    <p className="validate-feedback-invalid">Organization is required.</p>
                  )}
                </div>

                {/* Name */}
                <div className="form-group col-md-6 mb-3">
                  <label htmlFor="name">Name <span className="text-danger">*</span></label>
                  <input
                    id="name"
                    className="form-control"
                    placeholder="Workspace name"
                    {...register('name', { required: true, minLength: 3 })}
                  />
                  {errors.name?.type === 'required' && (
                    <p className="validate-feedback-invalid">Workspace name is required.</p>
                  )}
                  {errors.name?.type === 'minLength' && (
                    <p className="validate-feedback-invalid">
                      Workspace name must be at least 3 characters.
                    </p>
                  )}
                </div>

                {/* Workspace Code dropdown - no placeholder, filtered by tenant */}
                <div className="form-group col-md-6 mb-3">
                  <label htmlFor="code">Code <span className="text-danger">*</span></label>
                  <select
  id="code"
  className="custom-select"
  {...register('code', { required: true })}
>
  {availableWorkspaceCodes.map((item) => (
    <option key={item.code} value={item.code}>
      {item.name}
    </option>
  ))}
</select>
{errors?.code?.type === 'required' && (
  <p className="validate-feedback-invalid">Workspace code is required.</p>
)}


                  {errors.code && (
                    <p className="validate-feedback-invalid">Workspace code is required.</p>
                  )}
                </div>

                {/* Description */}
                <div className="form-group col-md-6 mb-3">
                  <label htmlFor="description">Description</label>
                  <input
                    id="description"
                    className="form-control"
                    placeholder="Workspace description"
                    {...register('description')}
                  />
                </div>

                <hr className="mb-0 mt-0" />
                <div className="form-actions">
                  <button className="btn btn-primary btn2" disabled={isUpdating} type="submit" >
                    Save
                  </button>
                  <button
                    className="btn btn-secondary btn2"
                    style={{ marginLeft: 10 }}
                    type="button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditWorkspace;
